// src/modules/sales/dashboard/widgets/shells/MrrTrendChart.tsx
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Briefcase } from "lucide-react";
import { apiFetch } from "@/modules/shared/lib/api-client";
import { linearForecast } from "@/modules/shared/dashboard/forecast";

type Trend = { points: { month: string; total: number }[] };

function formatMrr(value: number): string {
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `R$ ${Math.round(value / 1_000)}k`;
  return `R$ ${Math.round(value)}`;
}

export function MrrTrendChart() {
  const { data } = useQuery<Trend>({
    queryKey: ["widget", "/sales/dashboard/mrr-trend"],
    queryFn: () => apiFetch<Trend>("/sales/dashboard/mrr-trend"),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const allPoints = linearForecast(data?.points ?? [], 3);
  const actualPoints = allPoints.filter((p) => !p.projected);
  const forecastPoints = allPoints.filter((p, i, arr) => {
    if (p.projected) return true;
    const nextIsProjected = arr[i + 1]?.projected;
    return !!nextIsProjected;
  });

  return (
    <div className="ol-card flat col-span-2 flex flex-col gap-3">
      <div className="ol-card-head">
        <div className="ol-card-ic green"><Briefcase /></div>
        <div className="ol-card-title">
          <div className="ol-card-name"><span className="n">MRR 6 meses</span></div>
          {allPoints.some((p) => p.projected) && (
            <span className="ol-kpi-l" style={{ fontSize: 11 }}>+ projeção 3 meses</span>
          )}
        </div>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={allPoints}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fill: "#6b716b", fontSize: 11 }} />
            <YAxis
              allowDecimals={false}
              tick={{ fill: "#6b716b", fontSize: 11 }}
              tickFormatter={formatMrr}
            />
            <Tooltip formatter={(v: number) => formatMrr(v)} />
            {/* Actual line */}
            <Line
              data={actualPoints}
              type="monotone"
              dataKey="total"
              stroke="#10b981"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "var(--lime)", stroke: "#10b981", strokeWidth: 1.5 }}
              isAnimationActive={false}
            />
            {/* Forecast line (dashed) */}
            {forecastPoints.length > 1 && (
              <Line
                data={forecastPoints}
                type="monotone"
                dataKey="total"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={(props: any) => {
                  const { cx, cy, payload } = props;
                  if (!payload?.projected) {
                    return <circle key={`mrr-dot-${cx}`} cx={cx} cy={cy} r={4} fill="var(--lime)" stroke="#10b981" strokeWidth={1.5} />;
                  }
                  return <circle key={`mrr-dot-${cx}`} cx={cx} cy={cy} r={3} fill="#fff" stroke="#10b981" strokeWidth={1.5} strokeDasharray="2 2" />;
                }}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
